import React, { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue } from 'react';
import { FaSearch, FaChevronDown, FaCheck, FaTimes } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import axios from 'axios';
import { toast } from 'react-toastify';

const HsCodeSearchDropdown = ({
    value = '',
    onChange,
    onSelect,
    placeholder = 'Search HS Code...',
    required = false,
    disabled = false,
    className = '',
    error = '',
    showLabel = true,
    cacheKey = 'hs_codes_cache',
    timestampKey = 'hs_codes_timestamp',
    apiEndpoint = '/api/get/hscode',
}) => {
    const [hsCodes, setHsCodes] = useState([]);
    const [filteredHsCodes, setFilteredHsCodes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [localError, setLocalError] = useState('');
    const [selectedCode, setSelectedCode] = useState(value);
    const [isFocused, setIsFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const deferredSearchTerm = useDeferredValue(searchTerm);

    // Enhanced Professional Styles - Fixed border issues
    const styles = {
        wrapper: {
            position: 'relative',
            width: '100%',
        },
        label: {
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '0.35rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
        },
        labelRequired: {
            color: '#ef4444',
            marginLeft: '0.25rem',
        },
        inputWrapper: {
            position: 'relative',
            width: '100%',
        },
        input: {
            width: '100%',
            padding: '0.75rem 3.5rem 0.75rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '400',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderRadius: '0.75rem',
            backgroundColor: disabled ? '#f1f5f9' : '#ffffff',
            color: '#1e293b',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        },
        inputLoading: {
            backgroundColor: '#f8fafc',
            borderColor: '#cbd5e1',
        },
        inputWithValue: {
            borderColor: '#10b981',
            backgroundColor: '#f0fdf4',
        },
        iconWrapper: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            pointerEvents: 'none',
        },
        searchIcon: {
            color: '#94a3b8',
            fontSize: '0.875rem',
        },
        chevronIcon: {
            color: '#94a3b8',
            fontSize: '0.75rem',
            transition: 'transform 0.25s ease',
            pointerEvents: 'none',
        },
        chevronRotated: {
            transform: 'rotate(180deg)',
        },
        clearButton: {
            position: 'absolute',
            right: '3.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.875rem',
            padding: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease',
            pointerEvents: 'auto',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            zIndex: 2,
        },
        clearButtonHover: {
            backgroundColor: '#f1f5f9',
            color: '#ef4444',
        },
        dropdown: {
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            left: 0,
            right: 0,
            maxHeight: '340px',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
            border: '2px solid #e2e8f0',
            borderRadius: '0.75rem',
            boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.2), 0 4px 20px -8px rgba(0, 0, 0, 0.06)',
            zIndex: 1000,
            display: isOpen ? 'block' : 'none',
            animation: 'slideDown 0.2s ease-out',
        },
        dropdownHeader: {
            padding: '0.5rem 1rem',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8fafc',
            borderRadius: '0.75rem 0.75rem 0 0',
            position: 'sticky',
            top: 0,
            zIndex: 1,
        },
        dropdownHeaderText: {
            fontSize: '0.65rem',
            fontWeight: '600',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        dropdownHeaderCount: {
            fontSize: '0.6rem',
            fontWeight: '500',
            color: '#94a3b8',
            backgroundColor: '#f1f5f9',
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
        },
        dropdownItem: {
            padding: '0.6rem 1rem',
            cursor: 'pointer',
            borderBottom: '1px solid #f8fafc',
            transition: 'all 0.15s ease',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        dropdownItemSelected: {
            backgroundColor: '#d1fae5',
            borderLeft: '3px solid #10b981',
        },
        dropdownItemHighlighted: {
            backgroundColor: '#f1f5f9',
        },
        dropdownItemContent: {
            flex: 1,
            minWidth: 0,
        },
        codeText: {
            fontWeight: '600',
            color: '#1e293b',
            fontSize: '0.875rem',
        },
        descriptionText: {
            color: '#64748b',
            fontSize: '0.75rem',
            marginTop: '0.125rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        checkmark: {
            color: '#10b981',
            fontSize: '0.875rem',
            marginLeft: '0.5rem',
            flexShrink: 0,
        },
        errorText: {
            marginTop: '0.375rem',
            fontSize: '0.75rem',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
        },
        noResults: {
            padding: '2rem 1rem',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '0.875rem',
        },
        noResultsIcon: {
            fontSize: '2rem',
            marginBottom: '0.5rem',
            display: 'block',
        },
        noResultsText: {
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#64748b',
        },
        noResultsSubText: {
            fontSize: '0.75rem',
            color: '#94a3b8',
            marginTop: '0.25rem',
        },
        loadingContainer: {
            padding: '2rem 1rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
        },
        loadingSpinner: {
            width: '32px',
            height: '32px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
        },
        loadingText: {
            fontSize: '0.875rem',
            color: '#94a3b8',
            fontWeight: '500',
        },
        loadingSubText: {
            fontSize: '0.7rem',
            color: '#cbd5e1',
        },
        selectedBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.65rem',
            fontWeight: '600',
            color: '#10b981',
            backgroundColor: '#d1fae5',
            padding: '0.15rem 0.5rem',
            borderRadius: '9999px',
            marginLeft: '0.5rem',
        },
    };

    // Inject keyframe animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            @keyframes slideDown {
                from { 
                    opacity: 0;
                    transform: translateY(-8px) scale(0.98);
                }
                to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                }
            }
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Cache functions
    const getCachedData = useCallback(() => {
        const CACHE_DURATION = 24 * 60 * 60 * 1000;
        try {
            const cachedData = localStorage.getItem(cacheKey);
            const timestamp = localStorage.getItem(timestampKey);
            if (cachedData && timestamp) {
                const cacheTime = parseInt(timestamp);
                const now = Date.now();
                if (now - cacheTime < CACHE_DURATION) {
                    return JSON.parse(cachedData);
                }
            }
            return null;
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return null;
        }
    }, [cacheKey, timestampKey]);

    const saveToCache = useCallback((data) => {
        try {
            localStorage.setItem(cacheKey, JSON.stringify(data));
            localStorage.setItem(timestampKey, Date.now().toString());
        } catch (error) {
            console.error('Error saving to localStorage:', error);
        }
    }, [cacheKey, timestampKey]);

    // Search function
    const performSearch = useCallback((searchTerm, codes) => {
        if (!searchTerm || searchTerm.trim() === '') {
            return codes;
        }
        setIsSearching(true);
        const term = searchTerm.toLowerCase().trim();

        const results = codes.filter(item => {
            const code = item.hS_CODE?.toLowerCase() || '';
            const desc = item.description?.toLowerCase() || '';
            if (code === term || code.startsWith(term)) return true;
            if (desc === term || desc.startsWith(term)) return true;
            return code.includes(term) || desc.includes(term);
        });
        setIsSearching(false);
        return results;
    }, []);

    const debouncedSearch = useMemo(
        () => debounce((value, codes, callback) => {
            const results = performSearch(value, codes);
            callback(results);
        }, 150),
        [performSearch]
    );

    // Filter HS Codes
    useEffect(() => {
        if (deferredSearchTerm.trim() === '') {
            setFilteredHsCodes(hsCodes);
            setIsSearching(false);
            return;
        }
        debouncedSearch(deferredSearchTerm, hsCodes, (results) => {
            setFilteredHsCodes(results);
        });
        return () => {
            debouncedSearch.cancel();
        };
    }, [deferredSearchTerm, hsCodes, debouncedSearch]);

    // Reset highlighted index when search results change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [filteredHsCodes]);

    // Fetch HS Codes
    useEffect(() => {
        const fetchHsCodes = async () => {
            const cachedData = getCachedData();
            if (cachedData && cachedData.length > 0) {
                setHsCodes(cachedData);
                setFilteredHsCodes(cachedData);
                setLocalError('');
                setIsLoading(false);
                
                if (value) {
                    const matched = cachedData.find(item => item.hS_CODE === value);
                    if (matched) {
                        setSearchTerm(`${matched.hS_CODE} - ${matched.description.substring(0, 50)}...`);
                        setSelectedCode(value);
                    }
                }
                return;
            }

            setIsLoading(true);
            try {
                const response = await axios.get(apiEndpoint);
                let hsCodesData = [];
                if (response.data && response.data.data) {
                    hsCodesData = response.data.data;
                } else if (Array.isArray(response.data)) {
                    hsCodesData = response.data;
                } else {
                    hsCodesData = response.data;
                }

                if (hsCodesData && hsCodesData.length > 0) {
                    saveToCache(hsCodesData);
                    setHsCodes(hsCodesData);
                    setFilteredHsCodes(hsCodesData);
                    
                    if (value) {
                        const matched = hsCodesData.find(item => item.hS_CODE === value);
                        if (matched) {
                            setSearchTerm(`${matched.hS_CODE} - ${matched.description.substring(0, 50)}...`);
                            setSelectedCode(value);
                        }
                    }
                }
                setLocalError('');
            } catch (error) {
                console.error('Error fetching HS Codes:', error);
                setLocalError('Failed to load HS Codes');
                const staleCache = localStorage.getItem(cacheKey);
                if (staleCache) {
                    try {
                        const staleData = JSON.parse(staleCache);
                        if (staleData && staleData.length > 0) {
                            setHsCodes(staleData);
                            setFilteredHsCodes(staleData);
                            toast.warning('Using cached HS Codes (API unavailable)');
                        }
                    } catch (e) {
                        console.error('Error parsing stale cache:', e);
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchHsCodes();
    }, [getCachedData, saveToCache, apiEndpoint, cacheKey, value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setIsFocused(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update selected code when value prop changes
    useEffect(() => {
        if (value && value !== selectedCode) {
            setSelectedCode(value);
            const selected = hsCodes.find(item => item.hS_CODE === value);
            if (selected) {
                setSearchTerm(`${selected.hS_CODE} - ${selected.description.substring(0, 50)}...`);
            }
        }
    }, [value, hsCodes, selectedCode]);

    // Handle selection
    const handleSelect = useCallback((code) => {
        setSelectedCode(code);
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
        
        const selected = hsCodes.find(item => item.hS_CODE === code);
        if (selected) {
            const displayText = `${selected.hS_CODE} - ${selected.description.substring(0, 50)}...`;
            setSearchTerm(displayText);
            
            if (onSelect) {
                onSelect(selected);
            }
            if (onChange) {
                onChange(code);
            }
        }
    }, [hsCodes, onSelect, onChange]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setIsOpen(true);
                if (filteredHsCodes.length > 0) {
                    setHighlightedIndex(e.key === 'ArrowDown' ? 0 : filteredHsCodes.length - 1);
                }
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredHsCodes.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredHsCodes.length) {
                    const selected = filteredHsCodes[highlightedIndex];
                    handleSelect(selected.hS_CODE);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    }, [isOpen, filteredHsCodes, highlightedIndex, handleSelect]);

    // Handle input change - Allow typing and searching WITHOUT auto-selection
    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setIsOpen(true);
        setLocalError('');
        setHighlightedIndex(-1);
        
        // If input is cleared, clear the selection
        if (value.trim() === '') {
            setSelectedCode('');
            if (onChange) {
                onChange('');
            }
            if (onSelect) {
                onSelect(null);
            }
        }
    }, [onChange, onSelect]);

    // Handle input focus - Clear the input to allow fresh selection
    const handleFocus = useCallback(() => {
        setIsFocused(true);
        setSearchTerm('');
        setIsOpen(true);
        setHighlightedIndex(-1);
    }, []);

    // Handle clear button click
    const handleClear = useCallback((e) => {
        e.stopPropagation();
        setSearchTerm('');
        setSelectedCode('');
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (onChange) {
            onChange('');
        }
        if (onSelect) {
            onSelect(null);
        }
        inputRef.current?.focus();
    }, [onChange, onSelect]);

    // Get input border color based on state
    const getInputStyles = () => {
        let borderColor = '#e2e8f0';
        let backgroundColor = '#ffffff';
        let boxShadow = '0 1px 3px rgba(0, 0, 0, 0.04)';

        if (error || localError) {
            borderColor = '#ef4444';
        } else if (isFocused) {
            borderColor = '#6366f1';
            boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.12)';
        } else if (selectedCode && !isOpen) {
            borderColor = '#10b981';
            backgroundColor = '#f0fdf4';
            boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
        } else if (isOpen) {
            borderColor = '#6366f1';
            boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.12)';
        }

        if (isLoading) {
            backgroundColor = '#f8fafc';
            borderColor = '#cbd5e1';
        }

        if (disabled) {
            backgroundColor = '#f1f5f9';
        }

        return { borderColor, backgroundColor, boxShadow };
    };

    const inputStyles = getInputStyles();

    return (
        <div className={className} style={styles.wrapper} ref={dropdownRef}>
            {showLabel && (
                <label style={styles.label}>
                    HS Code
                    {required && <span style={styles.labelRequired}>*</span>}
                    {selectedCode && !isLoading && !error && !localError && (
                        <span style={styles.selectedBadge}>
                            <FaCheck size={8} /> Selected
                        </span>
                    )}
                </label>
            )}

            <div style={styles.inputWrapper}>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={isLoading ? 'Loading HS Codes...' : placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isLoading}
                    style={{
                        ...styles.input,
                        borderColor: inputStyles.borderColor,
                        backgroundColor: inputStyles.backgroundColor,
                        boxShadow: inputStyles.boxShadow,
                    }}
                    onMouseEnter={(e) => {
                        if (!disabled && !isLoading && !error && !localError && !isFocused) {
                            e.currentTarget.style.borderColor = '#818cf8';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isFocused && !isOpen && !error && !localError) {
                            e.currentTarget.style.borderColor = selectedCode ? '#10b981' : '#e2e8f0';
                        }
                    }}
                />

                {/* Clear Button - Show only when there's a selection */}
                {selectedCode && !isLoading && !isOpen && (
                    <button
                        style={styles.clearButton}
                        onClick={handleClear}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                            e.currentTarget.style.color = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#94a3b8';
                        }}
                        title="Clear selection"
                        type="button"
                    >
                        <FaTimes size={14} />
                    </button>
                )}

                <div style={styles.iconWrapper}>
                    {isLoading || isSearching ? (
                        <div style={{ 
                            width: '18px', 
                            height: '18px',
                            border: '2px solid #e2e8f0',
                            borderTop: '2px solid #6366f1',
                            borderRadius: '50%',
                            animation: 'spin 0.7s linear infinite',
                        }} />
                    ) : (
                        <FaSearch style={styles.searchIcon} />
                    )}
                    <div style={{
                        ...styles.chevronIcon,
                        ...(isOpen ? styles.chevronRotated : {}),
                    }}>
                        <FaChevronDown />
                    </div>
                </div>

                {/* Dropdown */}
                <div style={styles.dropdown}>
                    {/* Dropdown Header */}
                    {!isLoading && filteredHsCodes.length > 0 && (
                        <div style={styles.dropdownHeader}>
                            <span style={styles.dropdownHeaderText}>
                                {filteredHsCodes.length} HS Codes
                            </span>
                            <span style={styles.dropdownHeaderCount}>
                                {filteredHsCodes.length} found
                            </span>
                        </div>
                    )}

                    {/* Loading State Inside Dropdown */}
                    {isLoading && (
                        <div style={styles.loadingContainer}>
                            <div style={styles.loadingSpinner} />
                            <div style={styles.loadingText}>Loading HS Codes...</div>
                            <div style={styles.loadingSubText}>Please wait</div>
                        </div>
                    )}

                    {/* Results */}
                    {!isLoading && filteredHsCodes.length > 0 ? (
                        filteredHsCodes.slice(0, 100).map((item, index) => (
                            <div
                                key={item.hS_CODE}
                                style={{
                                    ...styles.dropdownItem,
                                    ...(item.hS_CODE === selectedCode ? styles.dropdownItemSelected : {}),
                                    ...(highlightedIndex === index ? styles.dropdownItemHighlighted : {}),
                                }}
                                onMouseEnter={(e) => {
                                    setHighlightedIndex(index);
                                    if (item.hS_CODE !== selectedCode) {
                                        e.currentTarget.style.backgroundColor = '#f8fafc';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (item.hS_CODE !== selectedCode) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                                onClick={() => handleSelect(item.hS_CODE)}
                            >
                                <div style={styles.dropdownItemContent}>
                                    <div style={styles.codeText}>
                                        {item.hS_CODE}
                                    </div>
                                    <div style={styles.descriptionText}>
                                        {item.description}
                                    </div>
                                </div>
                                {item.hS_CODE === selectedCode && (
                                    <div style={styles.checkmark}>
                                        <FaCheck />
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        !isLoading && (
                            <div style={styles.noResults}>
                                <span style={styles.noResultsIcon}>🔍</span>
                                <div style={styles.noResultsText}>
                                    {searchTerm ? 'No matching HS Codes found' : 'No HS Codes available'}
                                </div>
                                {searchTerm && (
                                    <div style={styles.noResultsSubText}>
                                        Try adjusting your search terms
                                    </div>
                                )}
                            </div>
                        )
                    )}
                    {filteredHsCodes.length > 100 && !isLoading && (
                        <div style={{ 
                            padding: '0.5rem 1rem', 
                            textAlign: 'center', 
                            color: '#94a3b8', 
                            fontSize: '0.7rem',
                            borderTop: '1px solid #f1f5f9',
                            backgroundColor: '#f8fafc',
                            borderRadius: '0 0 0.75rem 0.75rem',
                        }}>
                            Showing first 100 results ({filteredHsCodes.length} total)
                        </div>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {(error || localError) && (
                <div style={styles.errorText}>
                    <span>⚠️</span> {error || localError}
                </div>
            )}
        </div>
    );
};

export default HsCodeSearchDropdown;